import { action, computed, makeObservable, observable } from 'mobx';
import { JobStatusUpdateOptions, JobType, UpdateJobInput } from '@clipr/lib';
import { input, InputState } from '../../components/input';
import { TrainerTimelineState } from '../../components/trainerTimeline';
import { TrainerTranscriptsState } from '../../components/trainerTranscripts';
import { assertNotNull, Result } from '../../core';
import { notifyError, notifySuccess } from '../../services/notifications';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { JobModel, JobVideoTypeList, ValidatorResponse } from '../../entities';
import { Error, pageError } from '../../core/error';
import { VideoInformationWindowState } from '../../components/jobs/videoInformationWindowState';
import { JobErrorsWindowState, OpenArgs as JobErrorsOpenArgs } from '../../components/momentWindow/jobErrorsWindowState';
import { ConfirmationModalState } from './confirmationModalState';
import { AnalyticsEventTypes } from '../../services/analytics/analyticsSchema';
import { TrainerHotkeysController } from './trainerHotkeysController';
import { getApiResultJobError } from '../../api';
import { PlayerState } from '../../components/player';
import { TrainerRedactionState } from '../../components/trainerRedaction';

const VideoPrivacyItems = [
  'Shareable',
  'Private'
];

const VideoStatusItems = [
  'Waiting',
  'InReview',
  'Updated',
  'Done'
];

export type TrainerVideoPageParams = {
  jobId: string
}

export class TrainerVideoPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'setError' | 'setLoaded'>(this, {
      isLoading: observable,
      error: observable,
      jobId: observable,
      job: computed,
      isEnrichmentProcessing: computed,
      isTranscriptProcessing: computed,
      isJobFailed: computed,
      isJobInProgress: computed,
      load: action,
      invoke: action,
      unload: action,
      handleVideoStatusInputChange: action,
      handleVideoTypeInputChange: action,
      handleVideoPrivacyInputChange: action,
      updateJobPrivacy: action,
      reset: action,
      setError: action,
      setLoaded: action,
    });

    this.player = new PlayerState(store, {
      jobId: () => this.jobId,
      allowShare: false,
      showReactionButton: false,
      showAddMomentButton: true,
      showRedactionButton: () => !this.job?.isAudioSource && !!this.redaction?.canEdit,
      redactionMode: () => this.redaction?.isVisualMode ?? false,
      showCommentsBar: () => !!this.job?.redactionMoments.length,
      momentSource: 'Trainer',
      openMomentWindowOptions: {
        layout: 'Trainer'
      }
    });

    this.redaction = new TrainerRedactionState(store, {
      jobId: () => this.jobId,
      player: () => this.player,
    });
    this.player.listen(this.playerListener);

    this.timeline = new TrainerTimelineState(store, {
      jobId: () => this.jobId,
      player: () => this.player,
      redaction: () => this.redaction,
    });

    this.transcripts = new TrainerTranscriptsState(store, {
      jobId: () => this.jobId,
      player: () => this.player,
      redaction: () => this.redaction,
    });

    this.videoPrivacyInput = input(this, {
      name: 'videoPrivacy',
      selectorItems: VideoPrivacyItems,
      onChange: this.handleVideoPrivacyInputChange
    });
    this.videoStatusInput = input(this, {
      name: 'videoStatus',
      selectorItems: VideoStatusItems,
      onChange: this.handleVideoStatusInputChange
    });
    this.videoTypeInput = input(this, {
      name: 'videoType',
      selectorItems: JobVideoTypeList,
      onChange: this.handleVideoTypeInputChange
    });

    this.hotkeys = new TrainerHotkeysController(this.store, this);
  }

  isLoading = false;
  error: Error | null = null;

  jobId: string | null = null;
  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  get isEnrichmentProcessing(): boolean {
    return this.player.isEnrichmentProcessing;
  }

  get isTranscriptProcessing(): boolean {
    return this.player.isTranscriptProcessing;
  }

  get isJobFailed(): boolean {
    return this.job?.isFailed ?? false;
  }

  get isJobInProgress(): boolean {
    return this.job?.isInProgress ?? false;
  }

  readonly player: PlayerState;
  readonly redaction: TrainerRedactionState;
  readonly transcripts: TrainerTranscriptsState;
  readonly timeline: TrainerTimelineState;

  readonly videoPrivacyInput: InputState;
  readonly videoStatusInput: InputState;
  readonly videoTypeInput: InputState;

  readonly hotkeys: TrainerHotkeysController;

  /** Notifies the state that the page has been mounted and triggers the initialization tasks. */
  async load(params: TrainerVideoPageParams, _queryString?: string) {

    this.reset();
    this.isLoading = true;

    // TODO: re-enable Inverse theme (and its Default reset in reset()) once dark-mode text issues in windows are fixed
    // this.store.uiService.setThemeVariation('Inverse');
    
    const { jobId } = params;
    // const query = new URLSearchParams(queryString);

    const { store } = this;
    if (!jobId)
      return this.setError();

    this.jobId = jobId;

    const response: Result[] = await Promise.all([
      store.apiFetchJob(jobId, true),
      store.apiFetchSpeakers({ jobId })
    ]);

    const err = getApiResultJobError(response);
    if (err)
      return this.setError(err);

    // check if this is the response that came after the last request, otherwise ignore it
    if (jobId !== this.jobId) {
      console.debug(`Job ${jobId} has been discarded.`);
      return false;
    }

    if (!this.job)
      return this.setError();

    const teamId = this.job.ownerTeamId;
    if (teamId) {
      await store.teamManager.apiFetchTeam({ id: teamId });
    }

    this.videoPrivacyInput.loadValue(this.job?.isPublic ? 'Shareable' : 'Private');
    this.videoStatusInput.loadValue(this.job?.status);
    this.videoTypeInput.loadValue(this.job?.videoType);

    this.player.chrome.pin();

    this.setLoaded();
  }

  invoke = (type: string, payload?: unknown) => {

    switch (type) {
      case 'openVideoInformationWindow':
        if (!this.jobId) return;
        this.player.invoke('clearMomentStub');
        const videoInformationWindow = this.store.videoInformationWindow;
        const videoInformationWindowListener = (msg: Message<VideoInformationWindowState>) => {
          switch (msg.type) {
            case 'open':
              this.player.invoke('enterPause');
              break;

            case 'close':
              videoInformationWindow.unlisten(videoInformationWindowListener);
              this.player.invoke('exitPause');
              this.player.invoke('clearMomentStub');
              this.timeline.clearMultipleEdit(); // until handled by id
              this.videoStatusInput.value = this.job?.status || null;
              this.videoTypeInput.value = this.job?.videoType || null;
              break;

            case 'jobUpdated':
              videoInformationWindow.unlisten(videoInformationWindowListener);
              this.player.invoke('exitPause');
              this.player.invoke('clearMomentStub');
              this.timeline.clearMultipleEdit(); // until handled by id
              this.videoStatusInput.value = this.job?.status || null;
              this.videoTypeInput.value = this.job?.videoType || null;
              break;
          }
        }
        videoInformationWindow.listen(videoInformationWindowListener);
        videoInformationWindow.open({
          jobId: this.jobId
        });
        break;
      case 'openJobErrorsWindow': {
        const { jobId } = this;
        const { clipList, multipleActiveTrackTypes } = payload as JobErrorsOpenArgs;
        if (!jobId)
          return;
        this.player.invoke('clearMomentStub');
        const jobErrorsWindow = this.store.jobErrorsWindow;
        const jobErrorsWindowListener = (msg: Message<JobErrorsWindowState>) => {
          switch (msg.type) {
            case 'open':
              this.player.invoke('enterPause');
              break;

            case 'close':
              jobErrorsWindow.unlisten(jobErrorsWindowListener);
              this.player.invoke('exitPause');
              this.player.invoke('clearMomentStub');
              break;
          }
        }
        jobErrorsWindow.listen(jobErrorsWindowListener);
        jobErrorsWindow.open({
          jobId,
          clipList,
          multipleActiveTrackTypes,
        });
        break;
      }
      case 'openJobPrivacyConfirmationWindow':
        if (!this.jobId) return;
        this.player.invoke('clearMomentStub');
        const jobPrivacyConfirmationModal = this.store.confirmationModal;
        const jobPrivacyConfirmationModalListener = (msg: Message<ConfirmationModalState>) => {
          switch (msg.type) {
            case 'open':
              this.player.invoke('enterPause');
              break;

            case 'close':
              jobPrivacyConfirmationModal.unlisten(jobPrivacyConfirmationModalListener);
              this.player.invoke('exitPause');
              this.player.invoke('clearMomentStub');
              this.videoPrivacyInput.value = this.job?.isPublic ? 'Shareable' : 'Private';
              break;
          }
        }
        jobPrivacyConfirmationModal.listen(jobPrivacyConfirmationModalListener);
        jobPrivacyConfirmationModal.open({
          onSubmit: this.updateJobPrivacy,
          modalMessage: `Are you sure you want to make the video ${this.videoPrivacyInput.value}?`,
          title: 'Job Update Confirmation'
        });
        break;
      default:
        break;
    }

  }

  /** Notifies the state that the page has been unmounted and triggers the cleanup tasks. */
  unload() {
    this.redaction.reset();
    this.reset();
  }

  private playerListener = (msg: Message<PlayerState>) => {
    if (msg.type === 'toggleRedactionMode')
      this.redaction.toggleVisualMode();
  };

  handleVideoStatusInputChange = async () => {
    assertNotNull(this.jobId);
    const { job } = this;

    const value = this.videoStatusInput.value;
    const input: UpdateJobInput = {
      id: this.jobId,
      status: value as JobStatusUpdateOptions
    }

    if (value === 'Done') {
      if (!job)
        return notifyError(this, `Failed to set video status to '${value}'.`);

      const validator: ValidatorResponse | null = job.validateSetToDone();
      const { error, moments, multipleActiveTrackTypes } = validator;

      if (error) {
        this.videoStatusInput.value = job.status ?? null;
        this.invoke('openJobErrorsWindow', { clipList: moments, multipleActiveTrackTypes });
        return;
      }
      // if (clipErrorList.length > 0) {
      //   this.videoStatusInput.value = this.job?.status || null;
      //   this.invoke('openJobErrorsWindow', { clipErrorList });
      //   return;
      // }
    }

    const [, err] = await this.store.jobManager.apiUpdateJob(input);
    if (err) {
      this.videoStatusInput.value = this.job?.status || null;
      return notifyError(this, `Failed to set video status to '${value}'.`);
    }
    notifySuccess(this, `Changed video status to '${value}'.`);
  }

  handleVideoTypeInputChange = async () => {
    assertNotNull(this.jobId);

    const value = this.videoTypeInput.value;
    const input: UpdateJobInput = {
      id: this.jobId,
      videoType: value as JobType
    }
    const videoTypeLabel = JobVideoTypeList.find(i => i.value === value)?.label;

    const [, err] = await this.store.jobManager.apiUpdateJob(input);
    if (err) {
      this.videoTypeInput.value = this.job?.videoType || null;
      return notifyError(this, `Failed to set video type to '${videoTypeLabel}'.`);
    }
    notifySuccess(this, `Changed video type to '${videoTypeLabel}'.`);
  }

  handleVideoPrivacyInputChange = async () => {
    this.invoke('openJobPrivacyConfirmationWindow', { job: this.job });
  }

  updateJobPrivacy = async () => {
    assertNotNull(this.jobId);
    const value = this.videoPrivacyInput.value;
    const input: UpdateJobInput = {
      id: this.jobId,
      isPublic: value === 'Shareable' ? true : false
    }
    const [, err] = await this.store.jobManager.apiUpdateJob(input);
    if (err) {
      this.videoPrivacyInput.value = this.job?.isPublic ? 'Shareable' : 'Private';
      return notifyError(this, `Failed to set video privacy to '${value}'.`);
    }

    if (value === 'Shareable') {
      this.store.analyticsService.registerEvent(
        AnalyticsEventTypes.VideoMakePublicType,
        {
          jobId: this.jobId,
          job: this.job as JobModel
        }
      )
    }

    notifySuccess(this, `Video privacy changed to '${value}'.`);
  }

  // #region State helpers
  reset() {
    
    // this.store.uiService.setThemeVariation('Default'); — see Inverse theme note in load()

    this.videoTypeInput.reset();
    this.videoStatusInput.reset();

    this.player.reset();
    this.jobId = null;
    this.isLoading = false;
    this.error = null;
  }

  private setError(error?: Error) {
    if (!error)
      error = pageError();

    this.isLoading = false;
    this.error = error;
  }

  private setLoaded() {
    this.isLoading = false;
    this.error = null;
  }
  // #endregion
}
